import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/query-keys';
import { apiFetch } from '@/lib/api/client';
import type { BusinessTrip, TripUser } from './types';
import { mapTripListItem, type RawTripListItem } from '@/lib/api/adapters/trips';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 15;

interface TripListResponse {
  total_records?: number;
  page?: number;
  page_size?: number;
  total_pages?: number;
  trips?: unknown[];
}

export type TripSortField = 'name' | 'start_date' | 'end_date' | 'state';

export interface TripListOptions {
  page?: number;
  pageSize?: number;
  tripType?: 'local' | 'international';
  tripTypes?: ReadonlyArray<'local' | 'international'>;
  state?: string;
  states?: ReadonlyArray<string>;
  startDateFrom?: string;
  startDateTo?: string;
  endDateFrom?: string;
  endDateTo?: string;
  search?: string;
  sortBy?: TripSortField;
  sortOrder?: 'asc' | 'desc';
}

export interface TripListResult {
  trips: BusinessTrip[];
  totalRecords: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

function buildTripQueryParams(options: TripListOptions = {}) {
  const {
    page = DEFAULT_PAGE,
    pageSize = DEFAULT_PAGE_SIZE,
    tripType,
    tripTypes,
    state,
    states,
    startDateFrom,
    startDateTo,
    endDateFrom,
    endDateTo,
    search,
    sortBy,
    sortOrder = 'asc',
  } = options;

  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));

  const tripTypeFilters =
    Array.isArray(tripTypes) && tripTypes.length > 0 ? tripTypes : tripType ? [tripType] : [];

  tripTypeFilters.forEach((value, index) => {
    if (!value) {
      return;
    }
    if (index === 0) {
      params.set('trip_type', value);
    } else {
      params.append('trip_type', value);
    }
  });

  const stateFilters = Array.isArray(states) && states.length > 0 ? states : state ? [state] : [];

  stateFilters.forEach((value, index) => {
    if (!value) {
      return;
    }
    if (index === 0) {
      params.set('state', value);
    } else {
      params.append('state', value);
    }
  });

  if (sortBy) {
    params.set('sort_by', sortBy);
    params.set('sort_order', sortOrder);
  }

  if (startDateFrom) {
    params.set('start_date_from', startDateFrom);
  }

  if (startDateTo) {
    params.set('start_date_to', startDateTo);
  }

  if (endDateFrom) {
    params.set('end_date_from', endDateFrom);
  }

  if (endDateTo) {
    params.set('end_date_to', endDateTo);
  }

  if (search) {
    params.set('search', search);
  }

  return { params, page, pageSize } as const;
}

async function fetchTripsFromEndpoint(
  endpoint: string,
  options: TripListOptions = {}
): Promise<TripListResult> {
  const { params, page, pageSize } = buildTripQueryParams(options);

  const response = await apiFetch(`${endpoint}?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch trips: ${response.statusText}`);
  }

  const data = (await response.json()) as TripListResponse;
  const rawTrips = Array.isArray(data.trips) ? (data.trips as RawTripListItem[]) : [];
  const trips = rawTrips.map((item) => mapTripListItem(item));

  const totalRecords = Number.isFinite(data.total_records) ? Number(data.total_records) : 0;
  const currentPage = Number.isFinite(data.page) ? Number(data.page) : page;
  const currentPageSize = Number.isFinite(data.page_size) ? Number(data.page_size) : pageSize;
  const totalPages = Number.isFinite(data.total_pages)
    ? Number(data.total_pages)
    : Math.max(1, Math.ceil(totalRecords / currentPageSize));

  return {
    trips,
    totalRecords,
    page: currentPage,
    pageSize: currentPageSize,
    totalPages,
  };
}

export const useTrips = (options: TripListOptions = {}) => {
  return useQuery({
    queryKey: [...queryKeys.trips, options] as const,
    queryFn: () => fetchTripsFromEndpoint('/api/v1/business-trip-requests', options),
  });
};

export const useApprovedTrips = (options: TripListOptions = {}) => {
  return useQuery({
    queryKey: [...queryKeys.tripApprovals, options] as const,
    queryFn: () => fetchTripsFromEndpoint('/api/v1/business-trip-requests/approved', options),
  });
};

export type { BusinessTrip, TripUser };
